<script lang="ts">
  import { DateModel, DebtorModel } from "$models";
  import {
    BodyText,
    CardInline,
    Header,
    ListNavigationItem,
    Spacer,
    ValInputField,
    Ver,
  } from "$components";
  import type { Spot } from "$apis/parkito2/index.ts";
  import {
    Form,
    type FormInput,
    Validate,
  } from "$shared/lib/validation_next";

  type Props = {
    formOutput: FormInput<{
      capacity: number;
      debtorId: string;
      licensePlate: string;
      startDate: DateModel;
      accessCode?: string | null;
      spotId: string;
    }>;
    preview?: boolean;
    selectedDebtor: DebtorModel;
    spotGroup: Spot;
  };
  const {
    formOutput,
    preview = false,
    selectedDebtor,
    spotGroup,
  }: Props = $props();
  const initialValues = $formOutput.res.optional();
  const fieldStartDate = Form.field(
    Validate.date().and(
      Validate.dateInRange({
        min: new Date("2026-03-01"),
        msgMin:
          "Kan inte börja ett tillstånd på denna anläggning förrän 1a Mars 2026",
      }),
    ),
    initialValues?.startDate?.format?.("YYYY-MM-DD"),
  );

  const fieldLicensePlate = Form.field(
    Validate.len(
      2,
      undefined,
      "Felaktigt registreringsnummer",
    ),
    initialValues?.licensePlate,
  );

  const fieldCapacity = Form.field(
    Validate.integer().and(Validate.numInRange({
      min: 1,
      msgMin: "Måste ha minst ett tillstånd",
    })),
    initialValues?.capacity.toString() ?? "1",
  );
  const fieldAccessCode = spotGroup.accessCodeRef
    ? Form.field(
      Validate.required(`${spotGroup.accessCodeRef?.name} måste anges`),
      initialValues?.accessCode,
    )
    : Form.field(Validate.optional(Validate.any()));

  const form = Form.object({
    capacity: fieldCapacity,
    debtorId: Form.fixed(selectedDebtor.id),
    licensePlate: fieldLicensePlate,
    startDate: fieldStartDate,
    accessCode: fieldAccessCode,
    spotId: Form.fixed(spotGroup.id),
  });

  formOutput.subscribeTo(form);
</script>
{#if !preview}
  <CardInline>
    <Header size="4" light>Önskat startdatum</Header>
    <ValInputField
      field={fieldStartDate}
      type="date"
      reserveErrorMsgHeight
    />

    {#if !selectedDebtor.isPerson()}
      <Header size="4" light>Antal tillstånd</Header>
      <ValInputField
        reserveErrorMsgHeight
        field={fieldCapacity}
        type="number"
        limitWidth
      />
      <BodyText size="s"
      >OBS! Om du väljer fler än 1 tillstånd; glöm då inte att lägga in alla
        dina regnummer i appen.</BodyText>
      <Spacer minSpace="m" />
    {/if}

    <Header size="4" light>Ange registreringsnummer</Header>
    <ValInputField
      reserveErrorMsgHeight
      placeholder="ABC 123"
      field={fieldLicensePlate}
      limitWidth
    />

    {#if spotGroup.accessCodeRef}
      <Header size="4" light>{spotGroup.accessCodeRef.name}</Header>
      <ValInputField
        field={fieldAccessCode}
        limitWidth
        reserveErrorMsgHeight
        label={spotGroup.accessCodeRef.description ?? undefined}
      />
    {/if}
  </CardInline>
{:else}
  <Ver>
    <ListNavigationItem
      title={$fieldStartDate.res
        .map((r) => `Start: ${r.format("D MMM YYYY")}`)
        .optional() ?? "-"}
      description={$fieldCapacity.res.map((cap) => `Antal tillstånd: ${cap} st`)
        .optional()}
      labelRight={$fieldLicensePlate.res.optional() ?? undefined}
      isSingleItem
    >
    </ListNavigationItem>
  </Ver>
{/if}
